import * as React from 'react'

import { useParams } from 'react-router-dom'

import { Dropdown } from '@/ui/form/controls/Dropdown'
import { useQueryScannerSettings } from '@/lib/api/company/configuration/scanner'
import { fmt, useChartT } from '../i18n'
import {
  getPreferredScanner,
  setPreferredScanner,
  SCAN_BRIDGE_DOWNLOAD_URL,
  SCAN_BRIDGE_MIN_BUILD,
  useScannerBridge,
} from './useScannerBridge'

type Props = {
  onCancel: () => void
  onScanned: (files: File[]) => void
  // Błąd wgrywania z kontenera; bez tego powrót na ten ekran po nieudanym
  // uploadzie wyglądał, jakby nic się nie stało.
  uploadError?: string | null
}

// 1:1 z WPF ReadDataFromScannerCommandExecute → SelectScannerDialog: wybór skanera
// z listy sterowników, potem skan. Tyle że listę oddaje mostek, a nie
// ScannerProvider.Instance w procesie aplikacji.
export function StepScan({ onCancel, onScanned, uploadError }: Props) {
  const T = useChartT()
  const {
    status, scanners, error, scanning, progress, scan, cancel, retry,
    version, buildDate, outdated, fetchLog, seenBefore,
  } = useScannerBridge()
  const { companyId } = useParams<{ companyId: string }>()
  const settings = useQueryScannerSettings({ companyId })
  const [deviceId, setDeviceId] = React.useState<string | null>(getPreferredScanner)
  const [log, setLog] = React.useState<{ supported: boolean; lines: string[] } | null>(null)
  const [copied, setCopied] = React.useState(false)

  const toggleLog = async () => {
    if (log) { setLog(null); return }
    setLog(await fetchLog())
  }

  // Klient ma wkleić to serwisowi, więc kopiujemy komplet: wersję, platformę,
  // wykryte skanery i log - sam log bez kontekstu zmusza do dopytywania.
  const copyDiagnostics = async () => {
    const report = [
      `EasyTacho Scanner ${version ?? '?'} z ${buildDate || '?'} (wymagany od ${SCAN_BRIDGE_MIN_BUILD})`,
      `Skanery: ${scanners.length ? scanners.map((s) => s.name).join(', ') : 'brak'}`,
      error ? `Błąd: ${error}` : null,
      '',
      ...(log?.lines ?? []),
    ].filter((x) => x !== null).join('\n')

    try {
      await navigator.clipboard.writeText(report)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      setCopied(false)
    }
  }

  const selected = scanners.find((s) => s.deviceId === deviceId) ?? scanners[0] ?? null

  const pickScanner = (value: string) => {
    setDeviceId(value)
    setPreferredScanner(value)
  }

  const handleScan = async () => {
    if (!selected) return
    try {
      const files = await scan(selected, {
        brightness: settings.data?.scanBrightness,
        showScannerDialog: settings.data?.showScannerDialog,
      })
      if (files.length) onScanned(files)
    } catch {
      // komunikat siedzi w `error` z hooka
    }
  }

  return (
    <div className="tw-p-6">
      <h2 className="tw-mb-1 tw-text-lg tw-font-semibold tw-text-slate-900">
        {T('scanTitle', 'Skanuj tarczki')}
      </h2>
      <p className="tw-mb-4 tw-text-sm tw-text-slate-600">
        {T(
          'scanIntro',
          'Zeskanuj tarczki bezpośrednio ze skanera. Po zakończeniu skanowania'
            + ' zostaną wykryte tarczki i wczytane aktywności do edycji.',
        )}
      </p>

      {status === 'checking' && (
        <div className="tw-flex tw-items-center tw-gap-3 tw-rounded tw-border tw-border-slate-200 tw-bg-slate-50 tw-px-4 tw-py-6">
          <div className="tw-h-5 tw-w-5 tw-animate-spin tw-rounded-full tw-border-2 tw-border-slate-300 tw-border-t-blue-600" />
          <span className="tw-text-sm tw-text-slate-700">
            {T('scanBridgeChecking', 'Szukam skanera...')}
          </span>
        </div>
      )}

      {status === 'unavailable' && (
        <div className="tw-rounded tw-border tw-border-amber-200 tw-bg-amber-50 tw-px-4 tw-py-4">
          <p className="tw-mb-2 tw-text-sm tw-font-medium tw-text-amber-900">
            {T('scanBridgeMissing', 'Nie można połączyć się ze skanerem')}
          </p>
          <p className="tw-mb-3 tw-text-sm tw-text-amber-800">
            {seenBefore
              ? T(
                  'scanBridgeStoppedHint',
                  'Program EasyTacho Scanner już tu wcześniej działał, więc najpewniej'
                    + ' jest tylko wyłączony. Uruchom go z menu Start (EasyTacho →'
                    + ' EasyTacho Scanner) i kliknij „Sprawdź ponownie”. Jeśli go tam nie'
                    + ' ma, zainstaluj ponownie.',
                )
              : T(
                  'scanBridgeMissingHint',
                  'Żeby skanować tarczki z przeglądarki, na komputerze ze skanerem musi'
                    + ' działać program EasyTacho Scanner. Zainstaluj go, albo uruchom'
                    + ' z menu Start, jeśli już go masz, i kliknij „Sprawdź ponownie”.',
                )}
          </p>
          <div className="tw-flex tw-gap-2">
            <button
              type="button"
              onClick={retry}
              className={
                seenBefore
                  ? 'tw-rounded tw-bg-blue-600 tw-px-3 tw-py-1.5 tw-text-sm tw-text-white hover:tw-bg-blue-700'
                  : 'tw-rounded tw-border tw-border-slate-300 tw-bg-white tw-px-3 tw-py-1.5 tw-text-sm tw-text-slate-700 hover:tw-bg-slate-50'
              }
            >
              {T('scanBridgeRetry', 'Sprawdź ponownie')}
            </button>
            <a
              href={SCAN_BRIDGE_DOWNLOAD_URL}
              className={
                seenBefore
                  ? 'tw-rounded tw-border tw-border-slate-300 tw-bg-white tw-px-3 tw-py-1.5 tw-text-sm tw-text-slate-700 hover:tw-bg-slate-50'
                  : 'tw-rounded tw-bg-blue-600 tw-px-3 tw-py-1.5 tw-text-sm tw-text-white hover:tw-bg-blue-700'
              }
            >
              {seenBefore
                ? T('scanBridgeReinstall', 'Zainstaluj ponownie')
                : T('scanBridgeDownload', 'Pobierz program (Windows)')}
            </a>
          </div>
        </div>
      )}

      {status === 'no-scanner' && (
        <div className="tw-rounded tw-border tw-border-slate-200 tw-bg-slate-50 tw-px-4 tw-py-4">
          <p className="tw-mb-3 tw-text-sm tw-text-slate-700">
            {T(
              'scanNoScanner',
              'Nie znaleziono żadnego skanera. Sprawdź, czy skaner jest włączony'
                + ' i podłączony do komputera.',
            )}
          </p>
          <button
            type="button"
            onClick={retry}
            className="tw-rounded tw-border tw-border-slate-300 tw-bg-white tw-px-3 tw-py-1.5 tw-text-sm tw-text-slate-700 hover:tw-bg-slate-50"
          >
            {T('scanBridgeRetry', 'Sprawdź ponownie')}
          </button>
        </div>
      )}

      {scanning && (
        <div className="tw-mt-4 tw-flex tw-items-center tw-gap-3 tw-rounded tw-border tw-border-blue-200 tw-bg-blue-50 tw-px-4 tw-py-3">
          <div className="tw-h-5 tw-w-5 tw-animate-spin tw-rounded-full tw-border-2 tw-border-blue-200 tw-border-t-blue-600" />
          <span className="tw-text-sm tw-text-blue-800">
            {progress?.transferring
              ? fmt(T('scanTransferring', 'Pobieram zeskanowane strony ({0})...'), progress.pageCount)
              : progress && progress.pageCount > 0
                ? fmt(T('scanInProgressPages', 'Skanuję... (stron: {0})'), progress.pageCount)
                : T('scanInProgress', 'Skanuję...')}
          </span>
        </div>
      )}

      {(uploadError || status === 'error' || (error && !scanning)) && (
        <div className="tw-mt-4 tw-whitespace-pre-line tw-rounded tw-border tw-border-red-200 tw-bg-red-50 tw-px-3 tw-py-2 tw-text-sm tw-text-red-700">
          {uploadError || error || T('scanBridgeError', 'Błąd połączenia ze skanerem')}
        </div>
      )}

      {outdated && (
        <div className="tw-mb-4 tw-flex tw-flex-wrap tw-items-center tw-gap-3 tw-rounded tw-border tw-border-amber-200 tw-bg-amber-50 tw-px-4 tw-py-3">
          <span className="tw-text-sm tw-text-amber-900">
            {fmt(
              T('scanBridgeOutdated', 'Masz starszą wersję programu EasyTacho Scanner ({0}). Zalecana: {1}.'),
              version ?? '?',
              SCAN_BRIDGE_MIN_BUILD,
            )}
          </span>
          <a
            href={SCAN_BRIDGE_DOWNLOAD_URL}
            className="tw-rounded tw-bg-amber-600 tw-px-3 tw-py-1.5 tw-text-sm tw-text-white hover:tw-bg-amber-700"
          >
            {T('scanBridgeUpdate', 'Pobierz nowszą')}
          </a>
        </div>
      )}

      {status === 'ready' && (
        <div className="tw-flex tw-flex-col tw-gap-3">
          <label className="tw-text-sm tw-font-medium tw-text-slate-700">
            {T('scanScannerField', 'Skaner')}
          </label>
          <Dropdown
            options={scanners.map((s) => ({ label: s.name, value: s.deviceId }))}
            value={selected?.deviceId ?? null}
            onChange={pickScanner}
            disabled={scanning}
            placeholder={T('scanSelectScanner', 'Wybierz skaner')}
          />
          <p className="tw-text-sm tw-text-slate-600">
            {T(
              'scanTips',
              'Wskazówki: skanuj w 300 dpi, najlepiej czarno-biało albo w skali szarości, z zamkniętą'
                + ' pokrywą (białe tło). Tarczki nie powinny na siebie nachodzić ani wystawać poza'
                + ' krawędź skanu.',
            )}
          </p>
        </div>
      )}

      {(status === 'no-scanner' || status === 'error' || error) && (
        <div className="tw-mt-3">
          <button
            type="button"
            onClick={toggleLog}
            className="tw-text-sm tw-text-blue-700 tw-underline hover:tw-text-blue-900"
          >
            {log
              ? T('scanHideLog', 'Ukryj szczegóły techniczne')
              : T('scanShowLog', 'Pokaż szczegóły techniczne')}
          </button>
          {log && !log.supported && (
            <p className="tw-mt-2 tw-text-sm tw-text-slate-600">
              {T(
                'scanLogUnsupported',
                'Ta wersja programu EasyTacho Scanner nie potrafi pokazać logu.'
                  + ' Zainstaluj nowszą i spróbuj ponownie.',
              )}
            </p>
          )}

          {log && log.supported && (
            <>
              <div className="tw-mt-2 tw-flex tw-items-center tw-gap-2">
                <button
                  type="button"
                  onClick={copyDiagnostics}
                  className="tw-rounded tw-border tw-border-slate-300 tw-bg-white tw-px-3 tw-py-1 tw-text-sm tw-text-slate-700 hover:tw-bg-slate-50"
                >
                  {copied
                    ? T('scanLogCopied', 'Skopiowano')
                    : T('scanLogCopy', 'Kopiuj do schowka')}
                </button>
                <span className="tw-text-xs tw-text-slate-500">
                  {T('scanLogHint', 'Wklej to w zgłoszeniu do serwisu.')}
                </span>
              </div>
              <pre className="tw-mt-2 tw-max-h-64 tw-overflow-auto tw-rounded tw-bg-slate-900 tw-p-3 tw-text-xs tw-text-slate-100">
                {log.lines.length
                  ? log.lines.join('\n')
                  : T('scanLogEmpty', 'Log mostka jest pusty.')}
              </pre>
            </>
          )}
        </div>
      )}

      <div className="tw-mt-6 tw-flex tw-justify-end tw-gap-2">
        <button
          type="button"
          onClick={scanning ? cancel : onCancel}
          className="tw-rounded tw-border tw-border-slate-300 tw-bg-white tw-px-4 tw-py-2 tw-text-sm tw-text-slate-700 hover:tw-bg-slate-50"
        >
          {scanning ? T('scanAbort', 'Przerwij skanowanie') : T('cancel', 'Anuluj')}
        </button>
        <button
          type="button"
          disabled={status !== 'ready' || !selected || scanning || settings.isLoading}
          onClick={handleScan}
          className="tw-rounded tw-bg-blue-600 tw-px-4 tw-py-2 tw-text-sm tw-text-white hover:tw-bg-blue-700 disabled:tw-opacity-50"
        >
          {T('scanStart', 'Skanuj')}
        </button>
      </div>
    </div>
  )
}
